import { type AuthenticatedUser, scopeRolesToProject } from '@testflow/shared';
import { useSession } from './session.js';
import { useCurrentProject } from './use-project.js';

// #23 Tenant-Isolation im Frontend. `GET /v1/auth/me` liefert bewusst ALLE Rollen über ALLE
// Projekte (auth/me und der notifications-Router brauchen genau das). Wer daraus direkt
// `isAdmin`/`isTesterOnly` ableitet, urteilt projektübergreifend: Ein Tester in Projekt A, der in
// Projekt B Testmanager ist, bekäme in A eine volle Navigation, die der Server ihm anschließend
// mit 403/404 verweigert.
//
// Diese Datei ist die EINE Stelle, an der aus dem Sitzungs-User die projekt-gescopte Sicht wird.
// Die REGEL selbst liegt seit N3/N6 in `@testflow/shared` (`scopeRolesToProject`) — Client,
// Middleware (`apps/api/src/middleware/project-scope.ts`) und Mailversand
// (`apps/api/src/services/notifications/dispatch.ts`) rufen wörtlich dieselbe Funktion. Solange
// die Regel zweimal dastand, ist sie auseinandergelaufen; deshalb steht sie jetzt einmal.
//
// VIERTER Aufrufer (Review-Fund M6 — er fehlte in dieser Aufzählung): die Benachrichtigungsglocke,
// `apps/web/src/components/notification-bell.tsx`. Sie ruft `scopeUserToProject` BEWUSST je
// Benachrichtigung mit `n.project_id` — nicht `useScopedUser()` je URL. Grund: Die Glocke ist
// user-scoped und zeigt Meldungen aus ALLEN Projekten; welcher Deeplink richtig ist, hängt an dem
// Projekt der jeweiligen Meldung, nicht an dem, das gerade offen ist. Genau das war schon einmal
// falsch (N3: die Mail scopte nicht, die Glocke schon, und derselbe `defect_assigned` führte in
// beiden auf verschiedene Routen). Wer das hier auf `useScopedUser()` „vereinheitlicht", holt den
// Fehler zurück.

/**
 * Beschneidet die Rollen des Nutzers auf GENAU EIN Projekt (Regel: `scopeRolesToProject`).
 *
 * Ohne Projekt (noch nicht geladen / gar keins) bleibt keine Rolle übrig. Aufrufer, die zwischen
 * „lädt noch", „Fehler beim Laden" und „hat keins" unterscheiden müssen, nehmen `isLoading` bzw.
 * `isError` aus `useScopedUser()`.
 */
export function scopeUserToProject(
  user: AuthenticatedUser | null,
  projectId: string | undefined,
): AuthenticatedUser | null {
  if (!user) return null;
  return { ...user, roles: scopeRolesToProject(user.roles, projectId) };
}

/**
 * Der Sitzungs-User, beschnitten auf ein Projekt. Jeder Leser, der aus Rollen eine Sichtbarkeit
 * ableitet (`isAdmin`, `isTesterOnly`, `can(...)`), zieht ihn hier — nicht aus `useSession()`.
 *
 * N4: `projectId` hat VORRANG vor `useCurrentProject()`. Wer die Projekt-ID ohnehin schon als
 * Prop bekommt (alle Katalog-Tabs, scope-table, plan-substitutions …), reicht sie durch und wird
 * damit garantiert auf DAS Projekt gescopt, für das er gerendert wird. Ohne den Vorrang hängt die
 * Deckungsgleichheit allein daran, dass heute dasselbe Hook das Prop speist — `use-project.ts`
 * kündigt einen echten Projekt-Switcher als Folgeschritt an, und danach wäre ein für Projekt X
 * gerendertes Panel auf Projekt Y gescopt.
 */
export function useScopedUser(projectId?: string): {
  user: AuthenticatedUser | null;
  isLoading: boolean;
  isError: boolean;
} {
  const { user } = useSession();
  const { project, isLoading, isError } = useCurrentProject();
  return { user: scopeUserToProject(user, projectId ?? project?.project_id), isLoading, isError };
}
